import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Loader2, Package } from 'lucide-react';

// Status display config — shared with other components
const STATUS_CONFIG = {
  in_warehouse:  { label: 'En Bodega',     color: 'bg-amber-100 text-amber-700' },
  in_fastmax:    { label: 'En FastMax',    color: 'bg-blue-100 text-blue-700' },
  with_driver:   { label: 'Con Conductor', color: 'bg-purple-100 text-purple-700' },
  delivered:     { label: 'Entregado',     color: 'bg-green-100 text-green-700' },
  returned:      { label: 'Devuelto',      color: 'bg-red-100 text-red-700' },
};

/**
 * GlobalSearchBar
 *
 * Debounced search input that queries the backend after 400ms of inactivity.
 * Results appear in a dropdown below the input. Clicking a result
 * calls onSelectPackage with the full package object.
 */
export function GlobalSearchBar({
  onSearch,
  searchResults,
  searchLoading,
  searchQuery,
  setSearchQuery,
  onSelectPackage,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search trigger
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.trim().length < 2) {
      setIsOpen(false);
      setSearchQuery('');
      onSearch('');
      return;
    }

    debounceRef.current = setTimeout(() => {
      onSearch(value);
      setIsOpen(true);
    }, 400);
  }, [onSearch, setSearchQuery]);

  const handleClear = useCallback(() => {
    setInputValue('');
    setIsOpen(false);
    setSearchQuery('');
    onSearch('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, [setSearchQuery, onSearch]);

  const handleSelect = useCallback((pkg) => {
    setIsOpen(false);
    setInputValue('');
    setSearchQuery('');
    onSelectPackage(pkg);
  }, [onSelectPackage, setSearchQuery]);

  const statusCfg = (status) => STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-600' };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Input */}
      <div className="relative flex items-center">
        <Search
          size={16}
          className="absolute left-3 text-gray-400 pointer-events-none"
        />
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 2 && searchResults.length > 0 && setIsOpen(true)}
          placeholder="Buscar por código, destinatario, distrito, usuario..."
          className="w-full pl-9 pr-8 py-2 text-sm font-montserrat bg-gray-100 border border-transparent rounded-lg focus:outline-none focus:border-brand-primary focus:bg-white transition-all placeholder:text-gray-400"
        />
        {/* Clear or loading indicator */}
        <div className="absolute right-3">
          {searchLoading ? (
            <Loader2 size={14} className="animate-spin text-gray-400" />
          ) : inputValue ? (
            <button onClick={handleClear} className="text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          ) : null}
        </div>
      </div>

      {/* Dropdown results */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-80 overflow-y-auto">
          {searchResults.length === 0 && !searchLoading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400 font-montserrat">
              Sin resultados para "{searchQuery}"
            </div>
          ) : (
            <>
              {/* Result count header */}
              <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                <span className="text-xs font-montserrat text-gray-500">
                  {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''}
                </span>
              </div>

              {searchResults.map((pkg) => {
                const cfg = statusCfg(pkg.status);
                return (
                  <button
                    key={pkg.id}
                    onClick={() => handleSelect(pkg)}
                    className="w-full flex items-start gap-3 px-3 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
                  >
                    {/* Icon */}
                    <div className="shrink-0 w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center mt-0.5">
                      <Package size={14} className="text-brand-primary" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-montserrat font-extrabold text-sm text-brand-primary">
                          #{pkg.delivery_point_id || pkg.delivery_point_provincia_id || '—'}
                        </span>
                        {pkg.is_provincia && (
                          <span className="text-xs bg-purple-100 text-purple-600 font-montserrat font-semibold px-1.5 py-0.5 rounded">Prov</span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-montserrat font-semibold ${cfg.color}`}>
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 font-montserrat mt-0.5 truncate">
                        {pkg.recipient_name && `${pkg.recipient_name} · `}
                        {pkg.district && `${pkg.district} · `}
                        {pkg.store_user?.username}
                      </p>
                      <p className="font-mono text-xs text-gray-300 font-montserrat mt-0.5 truncate">{pkg.tracking_code}</p>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}